import { useQuery } from '@tanstack/react-query'
import { Activity, Clock, CheckCircle, AlertTriangle, XCircle } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { apiClient } from '@/lib/api-client'

interface ServiceStatus {
  name: string
  status: string
  responseTime: number | null
  uptime: number | null
}

interface Incident {
  id: string
  title: string
  status: string
  severity: string
  createdAt: string
  resolvedAt: string | null
}

interface PlatformStatusData {
  overallStatus: string
  services: ServiceStatus[]
  incidents: Incident[]
}

function statusIndicator(status: string) {
  switch (status) {
    case 'operational':
      return <span className="inline-block h-3 w-3 rounded-full bg-green-500" />
    case 'degraded':
      return <span className="inline-block h-3 w-3 rounded-full bg-yellow-500" />
    case 'partial_outage':
      return <span className="inline-block h-3 w-3 rounded-full bg-orange-500" />
    case 'major_outage':
      return <span className="inline-block h-3 w-3 rounded-full bg-red-500" />
    default:
      return <span className="inline-block h-3 w-3 rounded-full bg-gray-400" />
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'operational': return 'Operational'
    case 'degraded': return 'Degraded'
    case 'partial_outage': return 'Partial Outage'
    case 'major_outage': return 'Major Outage'
    default: return status
  }
}

function overallBanner(status: string) {
  switch (status) {
    case 'operational':
      return (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardContent className="flex items-center gap-3 py-4">
            <CheckCircle className="h-6 w-6 text-green-500" />
            <div>
              <p className="text-lg font-semibold text-green-500">All Systems Operational</p>
              <p className="text-sm text-muted-foreground">All services are running normally</p>
            </div>
          </CardContent>
        </Card>
      )
    case 'degraded':
      return (
        <Card className="border-yellow-500/30 bg-yellow-500/5">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertTriangle className="h-6 w-6 text-yellow-500" />
            <div>
              <p className="text-lg font-semibold text-yellow-500">Degraded Performance</p>
              <p className="text-sm text-muted-foreground">Some services are experiencing issues</p>
            </div>
          </CardContent>
        </Card>
      )
    default:
      return (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardContent className="flex items-center gap-3 py-4">
            <XCircle className="h-6 w-6 text-red-500" />
            <div>
              <p className="text-lg font-semibold text-red-500">Service Disruption</p>
              <p className="text-sm text-muted-foreground">One or more services are experiencing outages</p>
            </div>
          </CardContent>
        </Card>
      )
  }
}

function incidentSeverityBadge(severity: string) {
  switch (severity) {
    case 'critical': return <Badge variant="destructive">Critical</Badge>
    case 'major': return <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/20">Major</Badge>
    case 'minor': return <Badge variant="secondary">Minor</Badge>
    default: return <Badge variant="secondary">{severity}</Badge>
  }
}

function incidentStatusBadge(status: string) {
  switch (status) {
    case 'resolved': return <Badge variant="success">Resolved</Badge>
    case 'investigating': return <Badge variant="warning">Investigating</Badge>
    case 'identified': return <Badge variant="info">Identified</Badge>
    case 'monitoring': return <Badge variant="outline">Monitoring</Badge>
    default: return <Badge variant="secondary">{status}</Badge>
  }
}

export function PlatformStatusPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['platform-status'],
    queryFn: () => apiClient.get<PlatformStatusData>('/platform-status'),
    refetchInterval: 30000,
  })

  const platformData = data?.data

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Platform Status</h1>
        <p className="text-muted-foreground">Real-time health and availability of platform services</p>
      </div>

      {isLoading ? (
        <>
          <Skeleton className="h-20 w-full" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
          </div>
          <Skeleton className="h-48 w-full" />
        </>
      ) : !platformData ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Activity className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">Unable to load platform status</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {overallBanner(platformData.overallStatus)}

          <div>
            <h2 className="text-lg font-semibold mb-4">Service Status</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {platformData.services.map((service) => (
                <Card key={service.name}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium">{service.name}</span>
                      {statusIndicator(service.status)}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{statusLabel(service.status)}</p>
                    <div className="flex items-center justify-between text-sm">
                      {service.responseTime !== null && (
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {service.responseTime}ms
                        </span>
                      )}
                      {service.uptime !== null && (
                        <span className={`font-medium ${service.uptime >= 99.9 ? 'text-green-500' : service.uptime >= 99 ? 'text-yellow-500' : 'text-red-500'}`}>
                          {service.uptime.toFixed(2)}%
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4">Recent Incidents</h2>
            {platformData.incidents.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <CheckCircle className="h-8 w-8 text-green-500 mb-2" />
                  <p className="text-sm text-muted-foreground">No recent incidents</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {platformData.incidents.map((incident) => (
                  <Card key={incident.id}>
                    <CardContent className="flex items-center justify-between p-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{incident.title}</span>
                          {incidentStatusBadge(incident.status)}
                          {incidentSeverityBadge(incident.severity)}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(incident.createdAt).toLocaleString()}
                          </span>
                          {incident.resolvedAt && (
                            <span>Resolved: {new Date(incident.resolvedAt).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4">Historical Uptime</h2>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Uptime History</CardTitle>
                <CardDescription>90-day service availability overview</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-muted-foreground">Uptime chart will be displayed here</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  )
}
